import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import { fetchHorizonsForSettings } from '../utils/accessibleHorizons';
import '../styles/ViewsCRUD.css';

/**
 * ViewsCRUD - CRUD interface for managing views
 * Views are SQL snippets (filters/sorts) that can be applied to tables
 */
const ViewsCRUD = () => {
  const { user } = useAuth();
  const { activeSuffix } = useGroup();
  const [views, setViews] = useState([]);
  const [availableHorizons, setAvailableHorizons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editingView, setEditingView] = useState(null);
  const [formSuffix, setFormSuffix] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingView, setDeletingView] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    label: '',
    table_column: '',
    sql_syntax: '',
    view_type: 'filter',
    is_private: true
  });

  const resolveDefaultSuffix = useCallback(() => {
    if (activeSuffix && availableHorizons.some((h) => h.suffix === activeSuffix)) {
      return activeSuffix;
    }
    return availableHorizons[0]?.suffix || activeSuffix || '';
  }, [activeSuffix, availableHorizons]);

  // Load all views from ALL horizons
  const loadViews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      const { horizons, error: horizonError } = await fetchHorizonsForSettings(getAuthHeaders, user);
      if (horizonError) {
        setError(horizonError);
        setLoading(false);
        return;
      }
      if (!horizons || horizons.length === 0) {
        setError('No horizons available. Ask an administrator to assign horizons to your account.');
        setLoading(false);
        return;
      }

      setAvailableHorizons(horizons);
      
      // Load views from ALL horizons
      const allViews = [];
      for (const horizon of horizons) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(horizon.suffix)}`,
            { headers }
          );
          const data = await response.json();
          
          if (data.success && data.data) {
            const viewsWithHorizon = data.data.map(v => ({
              ...v,
              _suffix: horizon.suffix,
              _horizonName: horizon.description || horizon.suffix
            }));
            allViews.push(...viewsWithHorizon);
          }
        } catch (err) {
          console.warn(`Failed to load views from horizon ${horizon.suffix}:`, err);
        }
      }
      
      setViews(allViews);
    } catch (err) {
      console.error('Error loading views:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const handleCreateClick = () => {
    setEditingView(null);
    setEditorMode('create');
    setFormSuffix(resolveDefaultSuffix());
    setFormData({
      label: '',
      table_column: '',
      sql_syntax: '',
      view_type: 'filter',
      is_private: true
    });
    setValidationResult(null);
    setEditorOpen(true);
  };

  const handleEditClick = (view) => {
    setEditingView(view);
    setEditorMode('edit');
    setFormSuffix(view._suffix || resolveDefaultSuffix());
    setFormData({
      label: view.label,
      table_column: view.table_column,
      sql_syntax: view.sql_syntax,
      view_type: view.view_type,
      is_private: view.is_private !== undefined ? view.is_private : true
    });
    setValidationResult(null);
    setEditorOpen(true);
  };

  const handleDeleteClick = (view) => {
    setDeletingView(view);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingView || !deletingView._suffix) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(deletingView._suffix)}/${encodeURIComponent(deletingView.id)}`,
        { method: 'DELETE', headers }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete view');
      }
      setDeleteConfirmOpen(false);
      setDeletingView(null);
      loadViews();
    } catch (err) {
      console.error('Error deleting view:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleValidate = async () => {
    const suffix = editingView?._suffix || formSuffix || resolveDefaultSuffix();
    if (!suffix) {
      setValidationResult({
        success: false,
        valid: false,
        error: 'No horizon available for validation'
      });
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(suffix)}/validate`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            table_column: formData.table_column,
            sql_syntax: formData.sql_syntax,
            view_type: formData.view_type
          })
        }
      );
      const data = await response.json();
      setValidationResult(data);
    } catch (err) {
      console.error('Error validating view:', err);
      setValidationResult({
        success: false,
        valid: false,
        error: err.message
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const suffix = editingView?._suffix || formSuffix || resolveDefaultSuffix();
    if (!suffix) {
      alert('No horizon available to save view to');
      return;
    }

    try {
      const headers = await getAuthHeaders();
      let response;
      if (editorMode === 'create') {
        response = await fetch(
          `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(suffix)}`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(formData)
          }
        );
      } else {
        response = await fetch(
          `${API_BASE_URL}/casc/v1/api/views/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(editingView.id)}`,
          {
            method: 'PATCH',
            headers,
            body: JSON.stringify(formData)
          }
        );
      }
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save view');
      }
      setEditorOpen(false);
      setEditingView(null);
      loadViews();
    } catch (err) {
      console.error('Error saving view:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationResult(null); // Clear validation when form changes
  };

  const canSubmit = formData.label.trim() && formData.table_column.trim() && formData.sql_syntax.trim();

  return (
    <div className="views-crud-container">
      <div className="views-crud-header">
        <h3>
          <i className="fas fa-filter"></i> Views
        </h3>
        <p className="description">
          Manage all views across all horizons. Views are SQL snippets used as filters, sorts, or
          <strong> SQL compute</strong> (SELECT against transform rowsets). SQL compute Views are applied
          in Transformation pipelines for fiscal week, window totals, overtime, and similar calculations.
          New views default to your <strong>active horizon</strong>
          {activeSuffix ? <> (<code>{activeSuffix}</code>)</> : null}; Transformations only list Views
          from the same horizon as the ingestion group.
        </p>
        <button className="btn btn-primary" onClick={handleCreateClick}>
          <i className="fas fa-plus"></i> Add View
        </button>
      </div>

      {loading && (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading views...
        </div>
      )}

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {!loading && !error && views.length === 0 && (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No views found</p>
          <p className="empty-state-subtitle">Click "Add View" to create your first view</p>
        </div>
      )}

      {!loading && !error && views.length > 0 && (
        <div className="views-table">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Table Column</th>
                <th>Type</th>
                <th>Privacy</th>
                <th>SQL Syntax</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {views.map((view) => (
                <tr key={`${view._suffix}-${view.id}`}>
                  <td className="view-label">
                    {view.label}
                    <span className="horizon-badge-inline">{view._horizonName}</span>
                  </td>
                  <td className="view-column">{view.table_column}</td>
                  <td>
                    <span className={`badge badge-${view.view_type}`}>
                      {view.view_type}
                    </span>
                  </td>
                  <td>
                    {view.is_private ? (
                      <span style={{ color: '#9c27b0', fontWeight: 'bold' }}>
                        <i className="fas fa-lock"></i> Private
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>
                        <i className="fas fa-globe"></i> Public
                      </span>
                    )}
                  </td>
                  <td className="view-sql">
                    <code>{view.sql_syntax}</code>
                  </td>
                  <td className="view-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditClick(view)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteClick(view)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Editor Modal */}
      {editorOpen && (
        <div className="modal show">
          <div className="modal-content large">
            <div className="modal-header">
              <h2 className="modal-title">
                {editorMode === 'create' ? 'Create View' : 'Edit View'}
              </h2>
              <span className="close" onClick={() => setEditorOpen(false)}>&times;</span>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="view-horizon">Horizon *</label>
                  <select
                    id="view-horizon"
                    className="form-control"
                    value={formSuffix}
                    onChange={(e) => setFormSuffix(e.target.value)}
                    required
                    disabled={editorMode === 'edit'}
                  >
                    {availableHorizons.length === 0 && (
                      <option value="">No horizons available</option>
                    )}
                    {availableHorizons.map((h) => (
                      <option key={h.suffix} value={h.suffix}>
                        {h.description || h.suffix}
                        {h.suffix === activeSuffix ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Transformations only list Views from the ingestion group&apos;s horizon. Prefer your active horizon
                    {activeSuffix ? <> (<code>{activeSuffix}</code>)</> : null}.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="view-label">Label *</label>
                  <input
                    id="view-label"
                    type="text"
                    className="form-control"
                    value={formData.label}
                    onChange={(e) => handleFormChange('label', e.target.value)}
                    placeholder="Unique label for this view"
                    required
                    disabled={editorMode === 'edit'} // Labels are unique, don't allow editing
                  />
                  <small className="form-help">
                    A unique identifier for this view (e.g., "active_customers", "recent_orders")
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="view-column">Table Column *</label>
                  <input
                    id="view-column"
                    type="text"
                    className="form-control"
                    value={formData.table_column}
                    onChange={(e) => handleFormChange('table_column', e.target.value)}
                    placeholder="Table column name"
                    required
                  />
                  <small className="form-help">
                    The table column this view is associated with (e.g., "entries", "created_at")
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="view-type">View Type *</label>
                  <select
                    id="view-type"
                    className="form-control"
                    value={formData.view_type}
                    onChange={(e) => handleFormChange('view_type', e.target.value)}
                    required
                  >
                    <option value="filter">Filter</option>
                    <option value="sort">Sort</option>
                    <option value="sql">SQL / compute</option>
                  </select>
                  <small className="form-help">
                    Filter: WHERE clauses. Sort: ORDER BY. SQL / compute: SELECT against virtual table{' '}
                    <code>rows</code> (SQLite; used by Transformations).
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="view-sql">SQL Syntax *</label>
                  <textarea
                    id="view-sql"
                    className="form-control"
                    rows={formData.view_type === 'sql' ? 10 : 4}
                    value={formData.sql_syntax}
                    onChange={(e) => handleFormChange('sql_syntax', e.target.value)}
                    placeholder={
                      formData.view_type === 'filter'
                        ? "WHERE raw->>'status' = 'active'"
                        : formData.view_type === 'sort'
                          ? "ORDER BY raw->>'created_at' DESC"
                          : "SELECT rows.*, date(start_time, '-' || ((CAST(strftime('%w', start_time) AS INTEGER) + 7 - week_start_dow) % 7) || ' days') AS fiscal_week FROM rows"
                    }
                    required
                  />
                  <small className="form-help">
                    {formData.view_type === 'sql' ? (
                      <>
                        SQLite SELECT against table <code>rows</code> (columns = raw field names).
                        Window functions and <code>date()</code>/<code>strftime()</code> are supported.
                        You may omit <code>FROM rows</code>; it is added when missing.
                      </>
                    ) : (
                      <>
                        Filter: WHERE on <code>raw-&gt;&gt;&apos;…&apos;</code>. Sort: ORDER BY similarly.
                      </>
                    )}
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={(e) => handleFormChange('is_private', e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>
                      <i className="fas fa-lock" style={{ color: formData.is_private ? '#9c27b0' : 'var(--text-secondary)' }}></i>
                      {' '}Private (only visible to admins and owner)
                    </span>
                  </label>
                </div>

                {validationResult && (
                  <div className={`validation-result ${validationResult.valid ? 'success' : 'error'}`}>
                    <i className={`fas fa-${validationResult.valid ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {validationResult.valid ? (
                      <span>Validation passed: {validationResult.message}</span>
                    ) : (
                      <span>Validation failed: {validationResult.error || validationResult.warning}</span>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-secondary btn-validate"
                  onClick={handleValidate}
                  disabled={!canSubmit || validating}
                >
                  {validating ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Validating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i> Validate Syntax
                    </>
                  )}
                </button>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditorOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                >
                  <i className="fas fa-save"></i>{' '}
                  {editorMode === 'create' ? 'Create' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Confirm Delete</h2>
              <span className="close" onClick={() => setDeleteConfirmOpen(false)}>&times;</span>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the view <strong>{deletingView?.label}</strong>?</p>
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle"></i> This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewsCRUD;
